/**
 * 控制台的身份与授权模型。
 *
 * 分层原则（与 D1 表结构一一对应）：
 *   users          全局身份 —— 一人一条，邮箱唯一，跨组织复用
 *     └─ 成员关系   user_id × tenant_id，**角色在这里生效**
 *   tenants        组织（租户）—— 客户主体
 *
 * 口令、两步验证密钥、锁定状态属于「人」；角色与权限属于「成员关系」。
 * 因此一名用户可以既是某企业的 admin，又是另一个组织的 viewer。
 */

export type UserStatus = 'active' | 'suspended';

export type TenantKind = 'personal' | 'enterprise';

export type TenantStatus = 'active' | 'suspended';

/**
 * 组织审核状态。
 *
 * 个人组织注册即 `verified`；企业组织注册后为 `pending`，
 * 由运营后台审核后改为 `verified` 或 `rejected`。
 */
export type TenantVerificationStatus = 'pending' | 'verified' | 'rejected';

export type MembershipStatus = 'active' | 'suspended';

/** 组织内角色。授权作用域是组织，不是人。 */
export type OrgRole = 'owner' | 'admin' | 'member' | 'viewer';

export const ORG_ROLES: readonly OrgRole[] = ['owner', 'admin', 'member', 'viewer'];

/**
 * 权限位。命名规则：`<领域>.<动作>`。
 * 前端用它隐藏/禁用 UI（体验层），Worker 用它强制鉴权（安全层）。
 */
export type Permission =
  | 'org.view'
  | 'org.manage'
  | 'org.delete'
  | 'member.view'
  | 'member.invite'
  | 'member.manage'
  | 'role.view'
  | 'app.view'
  | 'app.manage'
  | 'audit.view'
  | 'audit.prune'
  | 'settings.manage'
  | 'billing.view'
  | 'billing.manage'
  | 'billing.invoice'
  | 'support.view'
  | 'support.create';

export const PERMISSIONS: readonly Permission[] = [
  'org.view',
  'org.manage',
  'org.delete',
  'member.view',
  'member.invite',
  'member.manage',
  'role.view',
  'app.view',
  'app.manage',
  'audit.view',
  'audit.prune',
  'settings.manage',
  'billing.view',
  'billing.manage',
  'billing.invoice',
  'support.view',
  'support.create',
];

// ------------------------------- 实体 --------------------------------------

export interface User {
  id: string;
  email: string;
  name: string;
  status: UserStatus;
  /** 邮箱是否已验证；未验证时不允许下单、邀请与生成 API Key */
  emailVerified: boolean;
  phone: string | null;
  /** 是否已启用 TOTP 两步验证 */
  totpEnabled: boolean;
  /** 为 true 时业务接口一律 403，只允许改密与登出 */
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  kind: TenantKind;
  status: TenantStatus;
  verificationStatus: TenantVerificationStatus;
  createdAt: string;
}

/** 当前用户在某组织里的成员关系。 */
export interface Membership {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  role: OrgRole;
}

/** 会话中呈现的全部身份信息。 */
export interface SessionUser {
  user: User;
  /** 当前上下文所在的组织 */
  tenant: Tenant;
  role: OrgRole;
  permissions: Permission[];
  /** 该用户可进入的全部组织，用于组织切换 */
  memberships: Membership[];
  /**
   * 为 true 时除「改密」与「登出」外所有接口都会返回 403。
   * 必须放在会话里：否则前端只能靠「随便调一个接口看看是不是 403」来猜。
   */
  mustChangePassword: boolean;
}

/** 登录 / 改密 / 读 me 的统一返回体。 */
export type LoginResponse = SessionUser;

// ------------------------------ 管理类实体 ----------------------------------

export interface MemberInfo {
  userId: string;
  email: string;
  name: string;
  role: OrgRole;
  status: MembershipStatus;
  userStatus: UserStatus;
  totpEnabled: boolean;
  lastLoginAt: string | null;
  joinedAt: string;
}

export interface InvitationInfo {
  id: string;
  email: string;
  role: OrgRole;
  invitedByName: string;
  expiresAt: string;
  acceptedAt: string | null;
  /** 仅创建邀请的响应里出现一次，之后不再返回明文令牌 */
  inviteUrl?: string;
  /** 创建邀请时邮件是否发送成功；失败时管理员可手工转达 inviteUrl */
  mailDelivered?: boolean;
}

export interface SessionInfo {
  id: string;
  current: boolean;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
}

export interface LoginEventInfo {
  id: string;
  at: string;
  email: string;
  result: 'success' | 'failure' | 'locked';
  reason: string | null;
  ip: string | null;
  userAgent: string | null;
}

export interface AuditEntryInfo {
  id: string;
  at: string;
  actorEmail: string;
  action: string;
  targetType: string;
  targetId: string | null;
  detail: string | null;
  result: 'success' | 'denied' | 'failure';
  ip: string | null;
}

export interface OidcClientInfo {
  clientId: string;
  name: string;
  redirectUris: string[];
  scopes: string[];
  status: 'active' | 'disabled';
  createdAt: string;
  lastUsedAt: string | null;
  /** 仅创建或轮换密钥的响应里出现一次 */
  clientSecret?: string;
}

/** 两步验证的设置阶段返回：密钥只在这里出现，之后只保留密文。 */
export interface TotpSetupResponse {
  secret: string;
  otpauthUrl: string;
}

export interface TotpEnableResponse {
  enabled: true;
  recoveryCodes: string[];
}

// --------------------------------- 错误 ------------------------------------

export interface ApiErrorBody {
  code: string;
  message: string;
  /** 校验失败时的字段级说明：`{ 字段名: 中文原因 }` */
  details?: Record<string, string>;
}

export type ApiResponse<T> = { ok: true; data: T } | { ok: false; error: ApiErrorBody };

// ------------------------------ 计费（镜像） --------------------------------

/**
 * 计费相关的展示类型。
 *
 * 权威定义在 `PAYMENT/shared/src/types.ts`（数据属于支付 Worker）。这里是**镜像**：
 * 跨工作区 import 会拖垮两边的构建，所以只镜像界面真正用到的字段。
 * 逐条对应与偏离记录在 `PAYMENT/docs/billing-integration.md`。
 */

export type BillingProductKind = 'service' | 'package' | 'subscription';

export interface BillingProduct {
  id: string;
  code: string;
  name: string;
  kind: BillingProductKind;
  /** 金额一律最小货币单位的整数（分）；不要在前端做浮点运算 */
  priceMinor: number;
  currency: string;
  /** 有效期天数；null 表示一次性服务，不产生长期权益 */
  termDays: number | null;
  entitlementCode: string | null;
  status: 'active' | 'retired';
  /** 以下字段由新版支付侧目录返回；旧数据可能缺省 */
  description?: string | null;
  category?: string;
  audience?: 'all' | 'personal' | 'enterprise';
  features?: string[];
  sortOrder?: number;
  /** 暂停销售：仍在架但下单入口关闭；旧数据可能缺省 */
  salesPaused?: boolean;
}

export type BillingOrderStatus = 'pending' | 'paid' | 'failed' | 'refunded' | 'closed';

export interface BillingOrder {
  id: string;
  tenantId: string;
  tenantName: string;
  kind: string;
  provider: string;
  productCode: string | null;
  amountMinor: number;
  currency: string;
  status: BillingOrderStatus;
  subject: string | null;
  createdAt: string;
  paidAt: string | null;
  closedAt: string | null;
}

export interface BillingEntitlement {
  tenantId: string;
  code: string;
  orderId: string | null;
  grantedAt: string;
  expiresAt: string | null;
}

export interface BillingSummary {
  tenantId: string;
  currency: string;
  /** 累计消费＝已付款订单之和（由支付侧从订单重算，因此天然幂等） */
  totalSpentMinor: number;
  paidOrderCount: number;
  lastPaidAt: string | null;
  entitlements: BillingEntitlement[];
}

/**
 * 下单后拿到的支付素材。
 *
 * `bill` 是 Base64URL 编码的 `Payment-Needed` 账单原文；`qrCodeUrl` / `qrCodeSvg` 只有在
 * 收款通道拿到「预下单/收银台」权限时才非空 —— 目前为空，因此界面必须如实说明
 * 「需要把账单交给买家侧的支付宝 AI付 完成支付」，而不是显示一个假的二维码。
 */
export interface BillingPaymentTicket {
  qrCodeUrl: string | null;
  qrCodeSvg: string | null;
  bill: string;
  expiresAt: string;
  /** 来自模拟通道时为 true：此时用户无法真正付款 */
  simulated: boolean;
  note?: string | null;
  /** 实际使用的支付通道 */
  provider?: string;
  /** 收银台跳转地址（网页支付）；有它时优先整页跳转 */
  redirectUrl?: string | null;
}

/** 支付通道与能力（支付侧注册表的镜像）。 */
export interface BillingProviderInfo {  code: string;
  label: string;
  capabilities: {
    qr: boolean;
    redirect: boolean;
    refund: boolean;
    query: boolean;
    maxAmountMinor: number;
    clientType: 'pc' | 'mobile' | 'both';
  };
  enabled: boolean;
  disabledReason?: string;
  /** 部署配置是否齐全（区分「没配」与「被运营禁用」） */
  configEnabled?: boolean;
  /** 运营后台是否临时启用了它 */
  flagEnabled?: boolean;
}

/** 平台开关：由运营后台统一下发，登录页 / 注册页在未登录时也要知道。 */
export interface PlatformFlags {
  registrationEnabled: boolean;
  loginEnabled: boolean;
}

export interface BillingCheckoutResult {
  order: BillingOrder;
  /** 订单不需要立刻支付时为 null */
  pay: BillingPaymentTicket | null;
}

/**
 * 计费 API Key（面向外部 AI / 服务的 `X-API-Key`）。
 *
 * 明文**绝不**出现在这里 —— 只有 `prefix` 与元数据；`secret` 只在创建响应里出现一次，
 * 与 OIDC 客户端的 `clientSecret` 同一套处理方式。
 */
export interface BillingApiKey {
  id: string;
  tenantId: string;
  name: string;
  /** 明文前缀（如 `hxpay_ab12cd34`），可安全展示 */
  prefix: string;
  permissions: string[];
  createdBy: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
}

/** 创建 API Key 的响应：`secret` 只出现这一次。 */
export interface BillingApiKeyCreated {
  apiKey: BillingApiKey;
  secret: string;
}

// ------------------------------ 费用与发票 ----------------------------------

export interface BillingStatementItem {
  productCode: string | null;
  productName: string | null;
  orderCount: number;
  amountMinor: number;
  refundedMinor: number;
}

export interface BillingMonthlyStatement {
  /** YYYY-MM（UTC） */
  month: string;
  currency: string;
  orderCount: number;
  amountMinor: number;
  refundedMinor: number;
  items: BillingStatementItem[];
}

export interface BillingStatements {
  tenantId: string;
  currency: string;
  statements: BillingMonthlyStatement[];
  totalAmountMinor: number;
  totalRefundedMinor: number;
}

export type BillingInvoiceStatus = 'requested' | 'issued' | 'rejected';

export interface BillingInvoice {
  id: string;
  tenantId: string;
  orderId: string | null;
  title: string;
  taxNo: string | null;
  email: string;
  amountMinor: number;
  currency: string;
  periodFrom: string;
  periodTo: string;
  status: BillingInvoiceStatus;
  rejectReason: string | null;
  issuedBy: string | null;
  issuedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ------------------------------ 自助注册 ------------------------------------

export type RegistrationMode = 'personal' | 'enterprise';

export interface RegisterInput {
  mode: RegistrationMode;
  email: string;
  password: string;
  name: string;
  /** 企业注册必填；个人组织名由服务端生成 */
  orgName?: string;
  contactName?: string;
  contactPhone?: string;
  industry?: string;
  website?: string;
  /** 已阅读并同意服务条款与隐私政策 */
  agreeTerms: boolean;
  /** Cloudflare Turnstile token；未启用校验时可为空 */
  turnstileToken?: string;
}

/**
 * 注册接口的返回。
 *
 * 与登录返回相同的会话结构；开发环境使用 log 邮件适配器时会额外带上
 * `devVerificationUrl`，方便本地不接真实邮箱也能完成验证。
 */
export interface RegisterResponse extends SessionUser {
  /** 仅开发环境（未配置真实邮件通道）返回 */
  devVerificationUrl?: string;
}

export interface TenantProfileInfo {
  tenantId: string;
  legalName: string;
  contactName: string;
  contactPhone: string;
  industry: string | null;
  website: string | null;
  licenseNo: string | null;
  rejectReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// -------------------------------- 工单 -------------------------------------

export type SupportTicketCategory = 'billing' | 'technical' | 'account' | 'other';
export type SupportTicketPriority = 'low' | 'normal' | 'high';
export type SupportTicketStatus = 'open' | 'pending' | 'resolved' | 'closed';

export interface SupportTicketMessageInfo {
  id: string;
  authorType: 'user' | 'admin' | 'system';
  authorName: string;
  body: string;
  createdAt: string;
}

export interface SupportTicketInfo {
  id: string;
  subject: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;
  createdBy: string | null;
  createdByName: string | null;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SupportTicketDetail extends SupportTicketInfo {
  messages: SupportTicketMessageInfo[];
}
